import { Alert, Button, Group, Modal, Stack, Table, Text } from '@mantine/core';
import { type ReactNode, useState } from 'react';
import { PiWarning } from 'react-icons/pi';
import { ReportedPaymentWarning } from '@/components/InvoiceDetailsModal/AmendInvoiceModal';
import { billToRows } from '@/components/InvoiceDetailsModal/billToRows';
import { billToRefreshOffered } from '@/components/InvoiceDetailsModal/changeActions';
import {
  InvoiceDetails,
  useBillToRefreshPreview,
  useRefreshBillTo,
} from '@/models/invoices';

const pct = (rate: number) =>
  `${(rate * 100).toFixed(2).replace(/\.00$/, '')}%`;

export type PayerRefresh = {
  /** Left out of the menu: the payer's details are what the document prints. */
  hidden: boolean;
  /** Why it cannot be done now; null when it can. */
  reason: string | null;
  open: () => void;
  /** The dialog, rendered outside the menu that opens it. */
  modal: ReactNode;
};

/**
 * Refresh payer details (spec 9.5): offered while nothing is collected and only when the payer's
 * details changed since the document printed them; the dialog shows what changes.
 */
export function usePayerRefresh(
  invoice: InvoiceDetails,
  /** Why the state or the permissions refuse it, before any preview. */
  gateReason: string | null,
): PayerRefresh {
  const allowed = gateReason == null;
  const preview = useBillToRefreshPreview(invoice.id, allowed);
  const refresh = useRefreshBillTo(invoice.offerId);
  const [opened, setOpened] = useState(false);
  const off = (reason: string | null, hidden = false): PayerRefresh => ({
    hidden,
    reason,
    open: () => {},
    modal: null,
  });

  if (!allowed) return off(gateReason);
  if (preview.isLoading) return off("Checking the payer's details...");
  if (preview.error) return off(`Not available: ${preview.error.message}`);
  const data = preview.data;
  const offered = billToRefreshOffered({
    status: invoice.status,
    paidAmount: invoice.paidAmount,
    mayWrite: allowed,
    refreshed: data?.refreshed,
  });
  if (!offered || !data?.refreshed) return off(null, true);

  const target = data.amended.find((a) => a.id === invoice.id);
  const vatBefore = Number(invoice.vatRate ?? 0);
  const vatChanges = target != null && Number(target.vatRate) !== vatBefore;
  const others = data.amended.filter((a) => a.id !== invoice.id);
  const rows = billToRows(data.before, data.after);

  const confirm = async () => {
    try {
      await refresh.mutateAsync(invoice.id);
    } catch {
      return;
    }
    setOpened(false);
  };

  return {
    hidden: false,
    reason: null,
    open: () => setOpened(true),
    modal: (
      <Modal
        opened={opened}
        onClose={() => setOpened(false)}
        size="lg"
        title="Update to the payer's current details"
      >
        <Stack gap="sm">
          <Text size="sm">
            The proforma gets bill-to version {data.version} from the payer as
            it is now. Version {data.version - 1} stays downloadable as issued.
            Nothing is sent.
          </Text>
          <Table withTableBorder fz="xs">
            <Table.Thead>
              <Table.Tr>
                <Table.Th />
                <Table.Th>On the proforma (v{data.version - 1})</Table.Th>
                <Table.Th>Payer now (v{data.version})</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {rows.map((r) => (
                <Table.Tr key={r.label}>
                  <Table.Td>{r.label}</Table.Td>
                  <Table.Td>{r.before}</Table.Td>
                  <Table.Td fw={r.before !== r.after ? 600 : undefined}>
                    {r.after}
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
          {data.amended
            .filter((a) => a.reportedPayments.length > 0)
            .map((a) => (
              <ReportedPaymentWarning
                key={a.id}
                reports={a.reportedPayments}
                invoiceNumber={a.invoiceNumber}
              />
            ))}
          {vatChanges && target && (
            <Alert color="orange" icon={<PiWarning />}>
              VAT changes for the refreshed party: {pct(vatBefore)} to{' '}
              {pct(Number(target.vatRate))}, total {target.currency}{' '}
              {Number(target.totalAmount).toFixed(2)}.
            </Alert>
          )}
          {others.length > 0 && (
            <Alert color="blue">
              Later installments of the same payer with nothing collected are
              updated too: {others.map((o) => o.invoiceNumber).join(', ')}.
            </Alert>
          )}
          {data.skipped.some((s) => s.reason) && (
            <Alert color="orange" icon={<PiWarning />}>
              <Text size="sm">
                These later installments need separate handling:
              </Text>
              {data.skipped
                .filter((s) => s.reason)
                .map((s) => (
                  <Text size="xs" key={s.invoiceId}>
                    Installment {s.seq}: {s.reason}
                  </Text>
                ))}
            </Alert>
          )}
          {refresh.error && (
            <Alert color="red" icon={<PiWarning />}>
              {refresh.error.message}
            </Alert>
          )}
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setOpened(false)}>
              Keep as is
            </Button>
            <Button loading={refresh.isPending} onClick={confirm}>
              Update bill-to
            </Button>
          </Group>
        </Stack>
      </Modal>
    ),
  };
}
